// TMOS Maintenance Mode Management Page
// Date: November 15, 2025
// Purpose: Platform admin page for managing maintenance mode

import React, { useState, useEffect } from 'react';
import { 
  WrenchScrewdriverIcon,
  PowerIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { maintenanceModeService, MaintenanceMode } from '@/services/maintenanceModeService';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';

export const MaintenanceModePage: React.FC = () => {
  const { t } = useLanguage();
  const [maintenance, setMaintenance] = useState<MaintenanceMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [showEnableForm, setShowEnableForm] = useState(false);
  const [formData, setFormData] = useState({
    message: '',
    scheduled_start_time: '',
    scheduled_end_time: ''
  });

  useEffect(() => {
    loadMaintenanceStatus();
  }, []);

  const loadMaintenanceStatus = async () => {
    try {
      setLoading(true);
      const data = await maintenanceModeService.getAdminStatus();
      setMaintenance(data);
    } catch (error: any) {
      console.error('Error loading maintenance status:', error);
      toast.error(error.response?.data?.message || 'Failed to load maintenance status');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async () => {
    if (!formData.message || !formData.scheduled_end_time) {
      toast.error(t('maintenance.requiredFields') || 'Message and end time are required');
      return;
    }

    try {
      setEnabling(true);
      await maintenanceModeService.enableMaintenance({
        message: formData.message,
        scheduled_start_time: formData.scheduled_start_time || undefined,
        scheduled_end_time: formData.scheduled_end_time
      });
      toast.success(t('maintenance.enabledSuccess') || 'Maintenance mode enabled successfully');
      setShowEnableForm(false);
      setFormData({ message: '', scheduled_start_time: '', scheduled_end_time: '' });
      loadMaintenanceStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to enable maintenance mode');
    } finally {
      setEnabling(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm(t('maintenance.confirmDisable') || 'Are you sure you want to disable maintenance mode?')) {
      return;
    }

    try {
      setDisabling(true);
      await maintenanceModeService.disableMaintenance();
      toast.success(t('maintenance.disabledSuccess') || 'Maintenance mode disabled successfully');
      loadMaintenanceStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to disable maintenance mode');
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-surface-bg)] flex items-center justify-center">
        <p className="text-[var(--color-secondary-text)]">{t('common.loading') || 'Loading...'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-surface-bg)]">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <WrenchScrewdriverIcon className="w-8 h-8 text-[var(--color-primary-bg)]" />
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-surface-text)]">
                {t('maintenance.title') || 'Maintenance Mode'}
              </h1>
              <p className="text-[var(--color-secondary-text)]">
                {t('maintenance.subtitle') || 'Manage system maintenance mode'}
              </p>
            </div>
          </div>
        </div>

        {/* Current Status */}
        <Card variant="outline" padding="md" className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PowerIcon className="w-5 h-5" />
              {t('maintenance.currentStatus') || 'Current Status'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {maintenance?.is_active ? (
              <div className="space-y-4">
                <div className="p-4 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/20 rounded-lg">
                  <p className="text-[var(--color-warning)] font-semibold mb-2">
                    {t('maintenance.active') || 'Maintenance mode is ACTIVE'}
                  </p>
                  <p className="text-[var(--color-warning)] whitespace-pre-wrap">{maintenance.message}</p>
                  {maintenance.scheduled_end_time && (
                    <p className="text-[var(--color-warning)] text-sm mt-2">
                      {t('maintenance.scheduledEnd') || 'Scheduled to end'}: {new Date(maintenance.scheduled_end_time).toLocaleString()}
                    </p>
                  )}
                </div>
                <Button
                  onClick={handleDisable}
                  disabled={disabling}
                  variant="danger"
                >
                  {disabling ? t('maintenance.disabling') || 'Disabling...' : t('maintenance.disable') || 'Disable Maintenance Mode'}
                </Button>
              </div>
            ) : (
              <div className="p-4 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-lg">
                <p className="text-[var(--color-success)] font-semibold">
                  {t('maintenance.inactive') || 'Maintenance mode is INACTIVE'}
                </p>
                <p className="text-[var(--color-success)] text-sm mt-2">
                  {t('maintenance.systemOperational') || 'System is operational'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Enable Maintenance Form */}
        {!maintenance?.is_active && (
          <Card variant="outline" padding="md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClockIcon className="w-5 h-5" />
                {t('maintenance.enableMaintenance') || 'Enable Maintenance Mode'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!showEnableForm ? (
                <Button
                  onClick={() => setShowEnableForm(true)}
                  className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
                >
                  {t('maintenance.enable') || 'Enable Maintenance Mode'}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('maintenance.message') || 'Maintenance Message'} *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={t('maintenance.messagePlaceholder') || 'Enter maintenance message...'}
                      rows={4}
                      className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('maintenance.scheduledStart') || 'Scheduled Start Time (Optional)'}
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_start_time}
                      onChange={(e) => setFormData({ ...formData, scheduled_start_time: e.target.value })}
                      className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                      {t('maintenance.scheduledEnd') || 'Scheduled End Time'} *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_end_time}
                      onChange={(e) => setFormData({ ...formData, scheduled_end_time: e.target.value })}
                      required
                      className="w-full px-3 py-2 border rounded-md bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)]"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleEnable}
                      disabled={enabling}
                      className="bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]"
                    >
                      {enabling ? t('maintenance.enabling') || 'Enabling...' : t('maintenance.enable') || 'Enable'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowEnableForm(false);
                        setFormData({ message: '', scheduled_start_time: '', scheduled_end_time: '' });
                      }}
                    >
                      {t('common.cancel') || 'Cancel'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};


